import { Box, Typography, IconButton, Collapse, Tabs, Tab, useMediaQuery, useTheme } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CustomEquipoWithTooltip from '@/DesignSystem/components/partidos/CustomEquipoWithTooltip';
import { getTeamImageById } from '@/helpers/images-helper';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import { getCompetitionImage } from '@/helpers/leagues-helper';
import { translate } from '@/data/fixture-transalations';
import {
    getTimeShortWithMinutesLongWithYear,
} from '@/helpers/datetime-helper';
import { useRouter } from 'next/router';
import { Rutas } from '@/constants/rutas';
import VerifiedIcon from '@/components/home/icons/verified-icon';
import RedDotBlinking from '@/components/shared/custom/in-progress';
import { useFixtureStatus } from '@/DesignSystem/hooks/fixtures/useFixtureStatus';
import ClockIcon from '@/components/shared/icons/clock-icon';
import { LEAGUES } from '@/constants/leagues';
import useRouterPathname from '@/DesignSystem/hooks/useRouterPathname';

// Constantes para las keys de las tabs
export const PARTIDO_TABS = {
    ALINEACIONES: 'alineaciones',
    PREDICCION: 'prediccion',
    PREVIA: 'previa',
    RESUMEN: 'previa-la-liga-fantasy',
    H2H: 'h2h',
    ESTADISTICAS: 'estadisticas',
    ESTADISTICAS_FANTASY: 'estadisticas-fantasy',
    ESTADISTICAS_FANTASY_EN_VIVO: 'estadisticas-fantasy-en-vivo',
};

const GoalBadge = ({ goals }) => {
    if (goals === undefined || goals === null) return null;
    return (
        <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            border="1px solid"
            borderColor="border.main"
            width={25}
            height={25}
            justifyContent="center"
            borderRadius={1}
        >
            <Typography fontSize={16}>{goals}</Typography>
        </Box>
    );
};

const PartidoInfoNavigation = memo(
    ({ fixture, mode, selectedTab = PARTIDO_TABS.ALINEACIONES, hideAlineacionesText = false, home, away }) => {
        const router = useRouter();
        const homeTeam = home || fixture?.home;
        const awayTeam = away || fixture?.away;
        const [open, setOpen] = useState(false);
        const isMobile = useMediaQuery('(max-width: 576px)');
        const leagueId = fixture.league?.id ?? fixture.league ?? fixture.leagueId;
        const isLaLiga = leagueId === LEAGUES.LA_LIGA;
        const id = fixture.fixture?.id ?? fixture.id ?? fixture.fixtureId;

        const isPrediccionDePartido = useRouterPathname().endsWith('/prediccion-de-partido');

        const theme = useTheme();

        const isPast = useMemo(() => new Date(fixture.date) < new Date(), [fixture.date]);

        const tabLinks = useMemo(() => {
            const links = [
                {
                    label: 'Alineaciones',
                    href: `${Rutas.PARTIDO}/${id}/alineaciones-probables`,
                    key: PARTIDO_TABS.ALINEACIONES,
                },
            ];

            if ((isLaLiga && !isPast) || isPrediccionDePartido) {
                links.push({
                    label: 'Predicción',
                    href: Rutas.PREDICCION_PARTIDO(id),
                    key: PARTIDO_TABS.PREDICCION,
                });
            }

            // Previa pública: verificar si alguno de los equipos tiene previa pública
            const hasPreviaPublica = homeTeam?.hasPreviaPublica || awayTeam?.hasPreviaPublica;

            if (hasPreviaPublica && id) {
                links.push({
                    label: 'Previa',
                    href: `${Rutas.PARTIDO}/${id}/previa-editores`,
                    key: PARTIDO_TABS.PREVIA,
                });
            }

            // Tab "Resumen" independiente - previa la liga fantasy
            if (isLaLiga && !isPast) {
                links.push({
                    label: 'Resumen',
                    href: `${Rutas.PARTIDO}/${id}/previa-la-liga-fantasy`,
                    key: PARTIDO_TABS.RESUMEN,
                });
            }

            // ✅ H2H siempre disponible
            links.push({
                label: 'H2H',
                href: `${Rutas.PARTIDO}/${id}/enfrentamientos-historicos`,
                key: PARTIDO_TABS.H2H,
            });

            if (isPast) {
                links.push(
                    {
                        label: 'Estadísticas',
                        href: `${Rutas.PARTIDO}/${id}/estadisticas-de-partido`,
                        key: PARTIDO_TABS.ESTADISTICAS,
                    }
                    // { label: 'Noticias', href: `${Rutas.PARTIDO}/${id}/noticias` }
                );

                links.push({
                    label: 'Puntuaciones Fantasy',
                    href: `${Rutas.PARTIDO}/${id}/estadisticas-la-liga-fantasy-en-vivo`,
                    key: PARTIDO_TABS.ESTADISTICAS_FANTASY_EN_VIVO,
                });
            }

            if (isPast && leagueId === LEAGUES.LA_LIGA) {
                links.push({
                    label: 'Estadísticas Fantasy',
                    href: `${Rutas.PARTIDO}/${id}/estadisticas-la-liga-fantasy`,
                    key: PARTIDO_TABS.ESTADISTICAS_FANTASY,
                });
            }

            return links;
        }, [id, isPast, isLaLiga, homeTeam, awayTeam]);

        const { isInProgress } = useFixtureStatus(fixture.status);

        const textBox = useMemo(() => {
            if (mode === 'confirmed') {
                return (
                    <Box display="flex" justifyContent="space-between" px={0.5}>
                        <Box display="flex" alignItems="center" gap={0.5} px={0.5}>
                            <VerifiedIcon width={12} height={12} />
                            <Typography fontSize={12}>Alineación confirmada</Typography>
                        </Box>

                        {isInProgress && (
                            <RedDotBlinking size={12} elapsed={`min ${fixture.elapsed}'`} fontSize={12} />
                        )}
                    </Box>
                );
            }

            if (mode === 'last') {
                return (
                    <Box display="flex" alignItems="center" gap={0.5} px={0.5}>
                        <ClockIcon width={12} height={12} />
                        <Typography fontSize={12}>Última alineación</Typography>
                    </Box>
                );
            }

            return (
                <Box display="flex" alignItems="center" gap={0.5} px={0.5}>
                    <ClockIcon width={12} height={12} />
                    <Typography fontSize={12}>Alineaciones probables</Typography>
                </Box>
            );
        }, [mode, isInProgress, fixture.elapsed]);

        return (
            <Box
                width="100%"
                sx={{
                    mb: 1,
                    border: `1px solid ${theme.palette.border.main}`,
                    borderRadius: 0.5,
                    position: 'relative',
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, ${theme.palette.border.accent2} 0%, ${theme.palette.border.accent2}40 60%, transparent 100%)`,
                        borderTopLeftRadius: '4px',
                        borderTopRightRadius: '4px',
                        pointerEvents: 'none',
                        zIndex: 1,
                    },
                }}
            >
                {/* Sección superior */}
                <Box
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    gap={1}
                    px={1}
                    borderTop="1px solid"
                    borderLeft={open ? 'none' : '1px solid'}
                    borderRight={open ? 'none' : '1px solid'}
                    borderBottom={open ? 'none' : '1px solid'}
                    borderColor="border.light"
                    onClick={() => setOpen(!open)}
                    sx={{ cursor: 'pointer', pt: 0.5 }}
                    pb={1}
                >
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <CustomImageComponent
                                src={getCompetitionImage(leagueId, 18)}
                                alt="liga"
                                width={12}
                                height={12}
                            />
                            <Typography fontSize={14}>
                                {translate(fixture.round, isMobile)} {' - '}
                            </Typography>
                            {fixture.date && (
                                <Typography fontSize={12}>
                                    {getTimeShortWithMinutesLongWithYear(fixture.date)}
                                </Typography>
                            )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {mode && !hideAlineacionesText && textBox}
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" width="100%" gap={0.5}>
                        {/* Equipo Local - 50% izquierda */}
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            width="50%"
                            justifyContent="flex-end"
                        >
                            <Typography fontSize={13} noWrap>
                                {homeTeam.name}
                            </Typography>
                            <CustomEquipoWithTooltip
                                teamId={homeTeam.id}
                                alt={homeTeam.name}
                                width={22}
                                height={22}
                                label={homeTeam.name}
                            />
                            {(homeTeam.goals || homeTeam.goals === 0) && <GoalBadge goals={homeTeam.goals} />}
                        </Box>

                        {/* Equipo Visitante - 50% derecha */}
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            width="50%"
                            justifyContent="flex-start"
                        >
                            {(awayTeam.goals || awayTeam.goals === 0) && <GoalBadge goals={awayTeam.goals} />}
                            <CustomEquipoWithTooltip
                                teamId={awayTeam.id}
                                src={getTeamImageById(awayTeam.id, 36)}
                                alt={awayTeam.name}
                                width={22}
                                height={22}
                                label={awayTeam.name}
                            />
                            <Typography fontSize={13} noWrap>
                                {awayTeam.name}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        px={1}
                        width="100%"
                    >
                        {fixture.stadium && (
                            <Typography fontSize={12}>
                                Estadio:{' '}
                                {typeof fixture.stadium === 'string'
                                    ? fixture.stadium
                                    : fixture.stadium.name || fixture.stadium}
                            </Typography>
                        )}
                        {fixture.referee && (
                            <Typography fontSize={12}>
                                Árbitro:{' '}
                                {typeof fixture.referee === 'string'
                                    ? fixture.referee
                                    : fixture.referee.name || fixture.referee}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Info adicional colapsable */}
                {fixture.venue && (
                    <Box px={1.5} py={1}>
                        <Typography fontSize={14}>
                            Ciudad:{' '}
                            {typeof fixture.venue === 'string'
                                ? fixture.venue
                                : fixture.venue.city || fixture.venue.name || ''}
                        </Typography>
                    </Box>
                )}

                {/* Tabs con navegación */}
                <Box borderBottom={'1px solid'} borderColor="border.light">
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                            gap: 0,
                        }}
                    >
                        <Tabs
                            value={selectedTab}
                            variant="fullWidth"
                            sx={{
                                minHeight: '32px',
                                width: '100%',
                                '& .MuiTabs-flexContainer': {
                                    flexWrap: 'wrap',
                                    gap: 0,
                                    justifyContent: 'center',
                                },
                                '& .MuiTab-root': {
                                    minHeight: '32px',
                                    padding: '4px 8px',
                                    fontSize: { xs: 14, md: 16 },
                                    textTransform: 'none',
                                    minWidth: 'unset',
                                    whiteSpace: 'nowrap',
                                    flex: '0 0 auto',
                                },
                                '& .MuiTabs-indicator': {
                                    display: 'none',
                                },
                                '& .MuiTab-root.Mui-selected': {
                                    borderBottom: '2px solid',
                                    borderColor: 'primary.main',
                                },
                            }}
                        >
                            {tabLinks.map((tab) => (
                                <Tab
                                    key={tab.key}
                                    value={tab.key}
                                    label={tab.label}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        router.push(tab.href);
                                    }}
                                    sx={{ cursor: 'pointer', textDecoration: 'none' }}
                                />
                            ))}
                        </Tabs>
                    </Box>
                </Box>
            </Box>
        );
    }
);

export default PartidoInfoNavigation;
